/**
 * UTM Parameter Tracking
 *
 * Captures UTM parameters from the URL on first landing and persists them
 * in sessionStorage so they survive SPA navigation. The captured values are
 * passed to auth/checkout API calls for attribution tracking.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const STORAGE_KEY = 'xrepl_utm';
const REF_STORAGE_KEY = 'xrepl_ref';

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/**
 * Call once on app initialization (e.g. in App.tsx useEffect).
 * Captures UTM params + referral code from the current URL if present.
 * Does NOT overwrite if already captured this session.
 */
export function captureUtmParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    // Capture UTM parameters
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (!existing) {
      const utm: Record<string, string> = {};
      let hasAny = false;
      for (const key of UTM_KEYS) {
        const val = params.get(key);
        if (val) {
          utm[key] = val;
          hasAny = true;
        }
      }
      if (hasAny) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
      }
    }

    // Capture referral code (?ref=XRPL-XXXX)
    const ref = params.get('ref');
    if (ref && !sessionStorage.getItem(REF_STORAGE_KEY)) {
      sessionStorage.setItem(REF_STORAGE_KEY, ref);
    }
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

/** Retrieve captured UTM params (returns empty object if none). */
export function getUtmParams(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Retrieve captured referral code (returns null if none). */
export function getReferralCode(): string | null {
  try {
    return sessionStorage.getItem(REF_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Build a flat metadata object suitable for passing to API calls. */
export function getAttributionMetadata(): Record<string, string> {
  const utm = getUtmParams();
  const ref = getReferralCode();
  const meta: Record<string, string> = {};
  for (const [k, v] of Object.entries(utm)) {
    if (v) meta[k] = v;
  }
  if (ref) meta.referral_code = ref;
  return meta;
}
